/**
 * shared/constants/index.ts
 * --------------------------
 * Application-wide constants shared across all Helix packages.
 * Covers API versioning, pagination defaults, supported providers,
 * sentiment thresholds, and feature flag keys.
 *
 * Consumers:
 *   - admin/frontend (imported directly)
 *   - admin/backend (mirrored in app/core/config.py)
 */

// ── API ───────────────────────────────────────────────────────────────────────

export const API_VERSION = 'v1' as const
export const API_BASE_PATH = `/api/${API_VERSION}` as const

// ── Pagination ────────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 25
export const MAX_PAGE_SIZE = 200

// ── Supported LLM Providers ───────────────────────────────────────────────────

export const LLM_PROVIDERS = ['openai', 'azure_openai', 'anthropic', 'google', 'groq', 'mistral', 'together', 'custom'] as const

export const PROVIDER_MODELS: Record<string, string[]> = {
  openai: [
    'gpt-4o',
    'gpt-4o-mini',
    'gpt-4-turbo',
    'gpt-4',
    'gpt-3.5-turbo',
    'o3',
    'o3-mini',
  ],
  anthropic: [
    'claude-opus-4-5',
    'claude-sonnet-4-5',
    'claude-haiku-4-5',
    'claude-opus-4',
    'claude-sonnet-4',
    'claude-haiku-4',
  ],
  google: [
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    'gemini-2.0-pro',
    'gemini-2.0-flash',
  ],
  groq: [
    'llama-3.3-70b-versatile',
    'llama-3.1-8b-instant',
    'mixtral-8x7b-32768',
  ],
  mistral: [
    'mistral-large-latest',
    'mistral-medium-latest',
    'mistral-small-latest',
    'open-mistral-nemo',
  ],
  azure_openai: [
    'gpt-4o',
    'gpt-4-turbo',
    'gpt-35-turbo',
  ],
}

export const EMBEDDING_MODELS: Record<string, string[]> = {
  openai: ['text-embedding-3-large', 'text-embedding-3-small', 'text-embedding-ada-002'],
  azure_openai: ['text-embedding-3-large', 'text-embedding-3-small', 'text-embedding-ada-002'],
  google: ['text-embedding-004'],
  mistral: ['mistral-embed'],
}

// ── Sentiment ─────────────────────────────────────────────────────────────────

export const SENTIMENT_THRESHOLDS = {
  POSITIVE: 0.3,    // score > 0.3 → positive
  NEGATIVE: -0.3,   // score < -0.3 → negative
  CRITICAL: -0.65,  // score < -0.65 → trigger critical alert
} as const

// ── Alert Cooldowns (minutes) ──────────────────────────────────────────────────

export const DEFAULT_ALERT_COOLDOWN_MINUTES = 30

// ── Knowledge Base ────────────────────────────────────────────────────────────

export const MAX_DOCUMENT_SIZE_MB = 50
// File extensions the upload wizard accepts. Must stay in step with the
// readers in Admin backend app/rag/ingestion.py — 'url' used to be listed here
// and is not an extension, so it put a meaningless ".url" in the file picker.
export const SUPPORTED_DOCUMENT_TYPES = [
  'pdf', 'docx', 'xlsx', 'csv', 'json', 'html', 'htm', 'md', 'txt',
] as const
export const DEFAULT_CHUNK_SIZE = 512          // tokens
export const DEFAULT_CHUNK_OVERLAP = 64        // tokens
export const DEFAULT_TOP_K = 10                // RAG retrieval

// ── Websocket / SSE ───────────────────────────────────────────────────────────

export const LIVEWALL_RECONNECT_INTERVAL_MS = 3000
export const LOG_STREAM_BUFFER_SIZE = 500       // max log lines kept in memory

// ── UI Timing ─────────────────────────────────────────────────────────────────

export const DEBOUNCE_DELAY_MS = 300
export const TOAST_DURATION_MS = 4000
export const REFETCH_INTERVAL_MS = 30_000       // background data refresh

// ── Route Paths ───────────────────────────────────────────────────────────────

export const ROUTES = {
  DASHBOARD: '/',
  LIVE_WALL: '/live-wall',
  CONVERSATIONS: '/conversations',
  ALERTS: '/alerts',
  LOGS: '/logs',
  KNOWLEDGE_BASE: '/knowledge-base',
  CHANNELS: '/channels',
  FLOWS: '/flows',
  PROMPTS: '/prompts',
  RETRIEVAL: '/retrieval',
  CRM: '/crm',
  VOICE_STYLE: '/voice-style',
  APPEARANCE: '/appearance',
  LLM_CONFIG: '/llm-config',
  LOGIN: '/login',
} as const

// ── Feature Flags (keys read from the backend /features endpoint) ─────────────

export const FEATURE_FLAGS = {
  AB_TESTING: 'ab_testing',
  VOICE_CHANNEL: 'voice_channel',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  MULTI_LANGUAGE: 'multi_language',
} as const

// ── Local-storage keys ────────────────────────────────────────────────────────

export const STORAGE_KEYS = {
  AUTH_TOKEN: 'helix_auth_token',
  REFRESH_TOKEN: 'helix_refresh_token',
  THEME: 'helix_theme',
  SIDEBAR_COLLAPSED: 'helix_sidebar_collapsed',
  LANG: 'helix_lang',
} as const
